import { Component, h, Host, Prop, State, Element, Listen } from '@stencil/core';
import {
  CmsLinkConfig,
  HorizontalPosition,
  Logger, MediaType,
  SectionBehavior,
  VerticalPositionBottom,
  VerticalPositionTop, VideoType, YoukuConfig, YoutubeConfig,
} from '../../global/_types';
import {
  getSectionBehavior,
  getYoukuUrl,
  isSmallerThanViewport,
  renderBlockMargin,
} from '../../global/_utils';
import { stencilEnv } from '../../global/_config';
import GLightbox from 'glightbox';
import { breakpoints, videoIdSelector } from '../../global/_constants';
import { adjustYoukuDOM, showConsent } from '../../global/controller/_lightbox';

@Component({
    tag: 'cms-header-stage',
    styleUrl: 'cms-header-stage.scss',
    shadow: true
})

export class CmsHeaderStage {
  @Element() host: HTMLDivElement;

  @Prop() behavior: string = '{}';
  @Prop() config: string;
  @Prop() debug: boolean = false;

  @State() isMobile: boolean = isSmallerThanViewport(breakpoints.ml);

  private logger: Logger;

  private options: CmsHeaderStageConfig;

  /* SectionBehavior */
  private sectionBehavior: SectionBehavior;

  private headlineTag: string;
  private subHeadlineTag: string;
  private lightbox: GLightbox;

  componentWillLoad() {
    this.logger = new Logger(this.debug, 'CmsHeaderStage');

    // Config
    this.options = JSON.parse(this.config) as CmsHeaderStageConfig;

    this.options = typeof (this.config) === 'string' ?
        JSON.parse(this.config) as CmsHeaderStageConfig :
        this.config;

    // Section-Behavior
    this.sectionBehavior = getSectionBehavior(this.behavior);

    this.headlineTag = `h${this.options.headlineLevel ? this.options.headlineLevel : 1}`;
    this.subHeadlineTag = `h${this.options.headlineLevel ? this.options.headlineLevel + 1 : 2}`;

    let videoElement = {};
    if (this.options.videoType ==='youtube') {
      videoElement = {
        'href': this.options.youtube.playerUrl.replace(videoIdSelector, this.options.videoId),
        'type': 'video',
        'source': 'youtube'
      };
    } else if (this.options.videoType ==='video') {
      videoElement = {
        'href': this.options.videoSource,
        'type': 'video',
        'source': 'local'
      };
    } else if (this.options.videoType ==='youku') {
      const src = getYoukuUrl(this.options.videoId, this.options.youku.playerUrl, this.options.youku.clientId, 'autoplay=1&rel=0');
      videoElement = {
        'content': '<iframe class="youku" src="' + src + '" frameborder="0" allowfullscreen="true"></iframe>'
      }
    }

      // Lightbox
    if (this.options.mediaType == 'video') {
      this.lightbox = GLightbox({
        elements: [videoElement],
        touchNavigation: true,
        autoplayVideos: true,
        loop: false,
        draggable: false,
        skin: 'clean',
        openEffect: 'zoom',
        closeEffect: 'zoom',
        videosWidth: this.getVideoWidth(),
        plyr: {
          css: `${stencilEnv.assetPath}plyr/plyr.css`,
          js: `${stencilEnv.assetPath}plyr/plyr.js`,
          config: {
            iconUrl: `${stencilEnv.assetPath}plyr/plyr.svg`,
            blankVideo: `${stencilEnv.assetPath}plyr/blank.mp4`,
            youtube: {
              noCookie: true,
              rel: 0,
              autoplay: 1
            }
          }
        }
      });
    }
  }

  componentDidLoad() {
    this.logger.trace('Component CmsHeaderStage loaded');

    // Adjust youku iframe (splide, gLightbox and plyr do not support youku)
    if (this.options.mediaType == 'video' && this.options.videoType == 'youku') {
      adjustYoukuDOM(this.lightbox);
    }

    // Force YouTube consent
    if (this.options.mediaType == 'video' && this.options.videoType == 'youtube') {
      showConsent(this.lightbox, 0);
    }
  }

  @Listen('resize', { target: 'window' })
  updateViewport() {
    if (this.isMobile != isSmallerThanViewport(breakpoints.ml)) {
      this.isMobile = isSmallerThanViewport(breakpoints.ml);
      this.lightbox.videosWidth = this.getVideoWidth();
      this.lightbox.reload();
    }
  }

  private getVideoWidth(): string {
    return this.isMobile ? '100v' : '80vw';
  }

  private getMediaContent(): string {
    if (!this.options.mediaType) {
      return '';
    }

    let media = '';
    switch (this.options.mediaType) {
      case 'picture':
        media = this.getPictureContent();
        break;
      case 'video':
        media = this.getVideoContent();
        break;
    }

    return (
      <div class={`media media-${this.options.mediaType}`}>
        <div class={`content-video-pic-container`}>
          { media }
        </div>
      </div>
    );
  }

  private getPictureContent(): string {
    return (
      this.options.picture ?
        <img src={this.options.picture} alt={this.options.pictureAlt}/>
        : ''
    );
  }

  private getVideoContent() {
    let preview = this.options.picture ? this.options.picture : '';

    if (!this.options.picture && this.options.videoId != null) {
      if (this.options.videoType === 'youtube') {
        preview = this.options.youtube.previewUrl.replace(videoIdSelector, this.options.videoId);
      } else if (this.options.videoType === 'youku') {
        preview = this.options.youku.previewUrl.replace(videoIdSelector, this.options.videoId);
      }
    }

    return (
      preview == ''
      ? '' :
      <div class={`video`}>
        <div class="video-button" tabindex="0" role="button"
             aria-label={this.options.labels?.videoButton ? this.options.labels?.videoButton : 'Video play button'}
             onClick={() => this.lightbox.open()}>
          <i class="rat-icon i-play" role="presentation"/>
        </div>
        <img src={preview} class={`video-preview`} alt={ this.options.videoCaption }/>
      </div>
   );
  }

  private getLinks(): string {
    if (!this.options.links || this.options.links.length < 1) {
      return '';
    }

    return (
      <ul class={`button-links`}>{
        this.options.links.map(linkConfig => (
          <li>
            <cms-link-or-button debug={this.debug} config={ linkConfig } />
          </li>
        ))
      }</ul>
    );
  }

  parseText(text: string): string {
    const regex = /##(\/?)(red|strong)##/gi;
    return text.replace(regex, '<$1$2>');
  }

  render() {
    // Default media position = left and video must not be center
    const useMediaPosition = this.options.mediaPosition != null
      && !((this.options.mediaType == 'video' || this.isMobile) && this.options.mediaPosition == 'center');
    // Content behavior
    const picturePosition = `picture-${useMediaPosition ? this.options.mediaPosition : 'left'}`;
    const textPosition = `text-${this.options.textPosition ? this.options.textPosition : 'right'}`;
    const headline = this.options.headline ? this.parseText(this.options.headline) : null;
    const subheadline = this.options.subheadline ? this.parseText(this.options.subheadline) : null;
    const pictureCentered = useMediaPosition && this.options.mediaPosition == 'center' && !this.isMobile;

    let rows = 0;
    if (this.options.headline) rows++;
    if (this.options.subheadline) rows++;
    if (this.options.text) rows++;

    return (
      <Host class={this.sectionBehavior.hostClass}>
        <section class={this.sectionBehavior.sectionClass} style={this.sectionBehavior.sectionStyle}>
          { renderBlockMargin(this.sectionBehavior, VerticalPositionTop) }
          <div class={`section-inner container ${picturePosition}`}>
            {/* Media center */}
            { pictureCentered ? this.getMediaContent() : '' }

            <div class={`content ${picturePosition} ${textPosition} content-span-${rows} font-size-${this.options.fontSize ? this.options.fontSize : 'default'}`}>
              {/* Headlines: parse headline for color and strong */}
              { this.options.headline ?
                <div class="headline">
                  { headline == this.options.headline
                    ? <this.headlineTag className={`headline-text`}>
                        { this.options.headline }
                      </this.headlineTag>
                    : <this.headlineTag className={`headline-text`} innerHTML={ headline } />
                  }
                </div>
                : ''
              }

              {/* Headlines: parse subheadline for color and strong */}
              { this.options.subheadline ?
                <div class="subheadline">
                  { subheadline == this.options.headline
                    ? <this.subHeadlineTag class={`headline-text`}>
                      { this.options.subheadline }
                    </this.subHeadlineTag>
                    : <this.subHeadlineTag className={`headline-text`} innerHTML={ subheadline } />
                  }
                </div>
                : ''
              }

              {/* Media left or right */}
              { !pictureCentered ? this.getMediaContent() : '' }

              {/* Text + Links */}
              <div class="text-and-links" innerHTML={this.options.text}>
                { this.getLinks() }
              </div>
            </div>
          </div>
          { renderBlockMargin(this.sectionBehavior, VerticalPositionBottom) }
        </section>
      </Host>
    );
  }
}


interface CmsHeaderStageConfig {
  headline?: string,
  subheadline?: string,
  text: string,
  headlineLevel?: number,
  mediaPosition?: HorizontalPosition,
  textPosition?: HorizontalPosition,
  fontSize?: string,
  pictureAlt?: string,
  picture?: string,
  videoCaption?: string,
  videoId?: string,
  videoSource?: string,
  links?: CmsLinkConfig[],
  mediaType: MediaType,
  videoType: VideoType,
  youtube?: YoutubeConfig,
  youku?: YoukuConfig,
  labels: {
    videoButton: string
  }
}
